import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { useAuth } from '../../../context/AuthContext';
import SiteNavBar from '../navigation/SiteNavBar';
import Footer from '../footer/Footer';
import CopyRight from '../footer/CopyRight';
import SubscriptionService from './SubscriptionService';
import SubscriptionPaymentForm from './SubscriptionPaymentForm';
import './SubscriptionSelection.css';

const SubscriptionSelection = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
    const [premiumPrice, setPremiumPrice] = useState({ formatted: '$79.00', period: 'per year' }); // Default fallback
    const [priceLoading, setPriceLoading] = useState(true);
    const [premiumPrices, setPremiumPrices] = useState([]); // All pricing tiers
    const [selectedTier, setSelectedTier] = useState(null); // Currently selected pricing tier
    const [showConfirmModal, setShowConfirmModal] = useState(false); // Confirmation modal state
    const [pendingTierChange, setPendingTierChange] = useState(null); // Stores tier change info for confirmation
    const [showLoyaltyTooltip, setShowLoyaltyTooltip] = useState(false); // Tooltip for loyalty perks

    console.log('SubscriptionSelection component rendered');
    console.log('SubscriptionSelection - user:', user);
    console.log('SubscriptionSelection - user role:', user?.role);
    console.log('SubscriptionSelection - user subscriptionStatus:', user?.subscriptionStatus);

    // Fetch premium pricing tiers from Stripe when component mounts
    useEffect(() => {
        const fetchPremiumPrices = async () => {
            try {
                const result = await SubscriptionService.getPremiumPrices();
                console.log('🔍 Premium prices result:', result);
                if (result.success && result.prices && result.prices.length > 0) {
                    setPremiumPrices(result.prices);
                    // Default to yearly (last in sorted array) or the default from API
                    const defaultPrice = result.prices.find(p => p.id === result.defaultPriceId)
                        || result.prices[result.prices.length - 1];
                    setSelectedTier(defaultPrice);
                    // Also set the legacy premiumPrice for compatibility
                    setPremiumPrice({
                        formatted: defaultPrice.formatted,
                        period: defaultPrice.period
                    });
                    console.log('✅ Premium prices set:', result.prices.length, 'tiers, default:', defaultPrice.tierName);
                } else {
                    // Fallback to single price endpoint
                    const singleResult = await SubscriptionService.getPremiumPrice();
                    if (singleResult.success) {
                        setPremiumPrice({
                            formatted: singleResult.formatted,
                            period: singleResult.period
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching premium prices:', error);
                // Keep fallback pricing
            } finally {
                setPriceLoading(false);
            }
        };

        fetchPremiumPrices();
    }, []);

    // Fetch current subscription when component mounts
    useEffect(() => {
        const fetchCurrentSubscription = async () => {
            if (user && user._id && token) {
                try {
                    const result = await SubscriptionService.getUserSubscription(user._id, token);
                    if (result.subscription) {
                        setCurrentSubscription(result.subscription);
                        // Pre-select the current plan
                        setSelectedPlan(result.subscription.status);
                    }
                } catch (error) {
                    console.error('Error fetching current subscription:', error);
                }
            }
        };

        fetchCurrentSubscription();
    }, [user, token]);

    // Sync selectedTier with user's current subscription tier
    useEffect(() => {
        if (premiumPrices.length > 0 && currentSubscription?.stripePriceId) {
            const userTier = premiumPrices.find(p => p.id === currentSubscription.stripePriceId);
            if (userTier) {
                setSelectedTier(userTier);
                setPremiumPrice({
                    formatted: userTier.formatted,
                    period: userTier.period
                });
                console.log('✅ Synced selectedTier to user subscription:', userTier.tierName);
            }
        }
    }, [premiumPrices, currentSubscription]);

    // Plans array with dynamic premium pricing
    const plans = [
        {
            id: 'basic',
            title: 'BASIC PLAN',
            price: '$0.00',
            period: 'Free',
            description: 'Perfect for getting started with essential features',
            features: [
                'Easy Online Shopping',
                'Free local pickup',
                'Order tracking',
                'Account dashboard',
                'Order history'
            ],
            popular: false
        },
        {
            id: 'premium',
            title: 'PREMIUM PLAN',
            price: selectedTier?.formatted || premiumPrice.formatted, // Dynamic from selected tier
            period: selectedTier?.period || premiumPrice.period,    // Dynamic from selected tier
            description: 'Unlock premium features with subscription',
            features: [
                'Exciting members-only loyalty rewards',
                'Easy Online Shopping',
                'Free local pickup',
                'Order tracking',
                'Account dashboard',
                'Order history'
            ],
            popular: true,
            hasTiers: premiumPrices.length > 1 // Flag to show tier selector
        }
    ];

    const handlePlanSelection = async (planId) => {
        if (!user || !token) {
            alert('Please log in to select a subscription plan.');
            navigate('/account/signin');
            return;
        }

        // Check if this is the current plan
        if (currentSubscription && currentSubscription.status === planId) {
            // For Premium, allow if changing to a different price tier
            if (planId === 'premium' && selectedTier?.id !== currentSubscription?.stripePriceId) {
                // Allow - user wants to change tier (handled below)
                console.log('User wants to change Premium tier from', currentSubscription?.stripePriceId, 'to', selectedTier?.id);
            } else {
                console.log('This is already your current plan');
                return;
            }
        }

        setSelectedPlan(planId);
        setLoading(true);

        try {
            if (planId === 'basic') {
                // Cancel Premium (downgrade to Basic plan)
                const result = await SubscriptionService.cancel(user._id, token);

                if (result.success) {
                    alert(result.message || 'Successfully cancelled Premium. You will retain access until the end of your billing period.');
                    setLoading(false);
                    // Redirect to account dashboard to show subscription status
                    navigate('/account/dashboard?tab=subscriptions');
                } else {
                    alert(`Failed to cancel Premium: ${result.error}`);
                    setLoading(false);
                }
            } else if (planId === 'premium') {
                // Check if this is a tier change (existing Premium user switching tiers)
                const isPremiumUser = currentSubscription && currentSubscription.status === 'premium';
                // If stripePriceId is null, we should treat it as potentially changing tiers
                // Also check if the selected tier is different from current
                const hasDifferentTier = currentSubscription?.stripePriceId 
                    ? selectedTier?.id !== currentSubscription.stripePriceId
                    : true; // If no priceId stored, allow tier selection
                const isChangingTier = isPremiumUser && hasDifferentTier;

                if (isChangingTier) {
                    // Get current tier name for confirmation message
                    const currentTier = premiumPrices.find(p => p.id === currentSubscription?.stripePriceId);
                    const currentTierName = currentTier?.tierName || 'current plan';

                    // Show confirmation modal instead of window.confirm
                    setPendingTierChange({
                        currentTierName,
                        newTierName: selectedTier?.tierName,
                        newPriceId: selectedTier?.id
                    });
                    setShowConfirmModal(true);
                    setLoading(false);
                    return;
                } else if (isPremiumUser) {
                    // User already has premium with same tier - show message
                    console.log('User already has this premium tier');
                    setLoading(false);
                    alert('You are already subscribed to this premium plan.');
                    return;
                } else {
                    // New Premium subscription - show payment form
                    const planDetails = plans.find(p => p.id === planId);
                    // Add selected tier info to plan details
                    setSelectedPlanDetails({
                        ...planDetails,
                        selectedTier: selectedTier,
                        priceId: selectedTier?.id
                    });
                    setShowPaymentForm(true);
                    setLoading(false);
                }
            }
        } catch (error) {
            console.error('Subscription error:', error);
            alert('An unexpected error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handlePaymentSuccess = (subscription) => {
        setShowPaymentForm(false);
        setSelectedPlanDetails(null);
        setCurrentSubscription(subscription);
        alert('Successfully upgraded to Premium Plan! Welcome to premium features.');
        // Optionally redirect to account dashboard
        navigate('/account/dashboard?tab=subscriptions');
    };

    const handlePaymentCancel = () => {
        setShowPaymentForm(false);
        setSelectedPlanDetails(null);
        setLoading(false);
    };

    // Handle confirmed tier change - show payment form to collect card details
    const handleConfirmTierChange = () => {
        if (!pendingTierChange) return;

        setShowConfirmModal(false);

        // Find the premium plan details
        const planDetails = plans.find(p => p.id === 'premium');

        // Set up plan details with tier change flag
        setSelectedPlanDetails({
            ...planDetails,
            selectedTier: selectedTier,
            priceId: pendingTierChange.newPriceId,
            isChangingTier: true, // Flag to indicate this is a tier change
            currentTierName: pendingTierChange.currentTierName,
            newTierName: pendingTierChange.newTierName
        });

        // Show the payment form
        setShowPaymentForm(true);
        setPendingTierChange(null);
    };

    // Handle cancel tier change
    const handleCancelTierChange = () => {
        setShowConfirmModal(false);
        setPendingTierChange(null);
        setLoading(false);
    };

    // If showing payment form, render it
    if (showPaymentForm) {
        return (
            <div>
                <SiteNavBar />
                <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-9 offset-lg-2 text-center">
                                <div className="hero-text">
                                    <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                                        <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                                        <h1>PREMIUM SUBSCRIPTION</h1>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="subscription-payment-container">
                    <SubscriptionPaymentForm
                        onSuccess={handlePaymentSuccess}
                        onCancel={handlePaymentCancel}
                        planDetails={selectedPlanDetails}
                    />
                </div>
                
                <Footer />
                <CopyRight />
            </div>
        );
    }

    return (
        <div>
            <SiteNavBar />
            <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-9 offset-lg-2 text-center">
                            <div className="hero-text">
                                <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                                    <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                                    <h1>MEMBERSHIP PLANS</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="subscription-container">
                <div className="subscription-header">
                    <div className="header-row">
                        {currentSubscription && (
                            <div className="current-plan-status">
                                <h1>Current Plan: {currentSubscription.getPlanDisplay()}</h1>
                            </div>
                        )}
                        <div className="choose-plan-section">
                            <h2>Choose your plan</h2>
                            <p>select the plan that best fits your needs</p>
                        </div>
                    </div>
                </div>

                <div className="plans-container">
                    {plans.map((plan) => {
                        // Determine button text and state for Premium plan
                        const isPremiumUser = currentSubscription && currentSubscription.status === 'premium';
                        const isCurrentTier = isPremiumUser && selectedTier?.id === currentSubscription?.stripePriceId;

                        let buttonText = 'SELECT';
                        let isDisabled = loading;

                        if (plan.id === 'premium') {
                            if (isCurrentTier) {
                                buttonText = 'CURRENT PLAN';
                                isDisabled = true;
                            } else if (isPremiumUser) {
                                buttonText = 'CHANGE PLAN';
                            }
                        } else if (plan.id === 'basic') {
                            if (currentSubscription && currentSubscription.status === 'basic') {
                                buttonText = 'CURRENT PLAN';
                                isDisabled = true;
                            }
                        }

                        return (
                            <div
                                key={plan.id}
                                className={`plan-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
                            >
                                {plan.popular && (
                                    <div className="popular-badge">Most Popular</div>
                                )}

                                <div className="plan-header">
                                    <h2>{plan.title}</h2>

                                    {/* Pricing Tier Tabs - only inside Premium card */}
                                    {plan.id === 'premium' && premiumPrices.length > 1 && (
                                        <div className="pricing-tier-selector in-card">
                                            <div className="tier-tabs">
                                                {premiumPrices.map((tier) => (
                                                    <button
                                                        key={tier.id}
                                                        className={`tier-tab ${selectedTier?.id === tier.id ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedTier(tier);
                                                            setPremiumPrice({
                                                                formatted: tier.formatted,
                                                                period: tier.period
                                                            });
                                                        }}
                                                    >
                                                        <span className="tier-name">{tier.tierName}</span>
                                                        {tier.savingsPercent > 0 && (
                                                            <span className="tier-savings">Save {tier.savingsPercent}%</span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="price">
                                        <span className="currency">{plan.price}</span>
                                        <span className="period">{plan.period}</span>
                                    </div>
                                    <p className="description">{plan.description}</p>
                                </div>

                                <div className="plan-features">
                                    <ul>
                                        {plan.features.map((feature, index) => (
                                            <li 
                                                key={index}
                                                className={plan.id === 'premium' && index === 0 ? 'feature-with-tooltip' : ''}
                                                onMouseEnter={() => {
                                                    if (plan.id === 'premium' && index === 0) {
                                                        setShowLoyaltyTooltip(true);
                                                    }
                                                }}
                                                onMouseLeave={() => {
                                                    if (plan.id === 'premium' && index === 0) {
                                                        setShowLoyaltyTooltip(false);
                                                    }
                                                }}
                                            >
                                                {feature}
                                                {plan.id === 'premium' && index === 0 && (
                                                    <span className="info-indicator-circle" title="Hover for more info">?</span>
                                                )}
                                                {plan.id === 'premium' && index === 0 && showLoyaltyTooltip && (
                                                    <div className="loyalty-tooltip">
                                                        <div className="tooltip-content">
                                                            <p><strong>$5.00 shopping credit</strong> every 5th Order, with more perks as we grow.</p>
                                                            <p className="tooltip-note">Shopping every other week can earn back the membership fee!!!</p>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <button
                                    className={`select-btn ${selectedPlan === plan.id ? 'selected' : ''} ${
                                        isDisabled && buttonText === 'CURRENT PLAN' ? 'current-plan' : ''
                                    }`}
                                    onClick={() => handlePlanSelection(plan.id)}
                                    disabled={isDisabled}
                                >
                                    {loading ? 'Processing...' : buttonText}
                                </button>
                            </div>
                        );
                    })}
                </div>
                
                <div className="subscription-footer">
                    <p>You can change your plan at any time from your account settings.</p>
                    <div style={{ marginTop: '20px' }}>
                        <button 
                            onClick={() => navigate('/shop')}
                            style={{
                                background: '#F28123',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '4px',
                                fontSize: '16px',
                                cursor: 'pointer'
                            }}
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal for Tier Change */}
            <Modal
                isOpen={showConfirmModal}
                onRequestClose={handleCancelTierChange}
                contentLabel="Confirm Plan Change"
                ariaHideApp={false}
                className="confirm-modal-content"
                overlayClassName="confirm-modal-overlay"
            >
                <div className="confirm-modal-header">
                    <h3>Change Subscription Plan</h3>
                </div>
                <div className="confirm-modal-body">
                    <p>Are you sure you want to change your plan from <strong>{pendingTierChange?.currentTierName}</strong> to <strong>{pendingTierChange?.newTierName}</strong>?</p>
                    <p className="confirm-modal-note">Your current subscription will be cancelled and a new subscription will start immediately.</p>
                </div>
                <div className="confirm-modal-actions">
                    <button
                        className="cancel-btn"
                        onClick={handleCancelTierChange}
                    >
                        Cancel
                    </button>
                    <button
                        className="confirm-btn"
                        onClick={handleConfirmTierChange}
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Yes, Change Plan'}
                    </button>
                </div>
            </Modal>

            <Footer />
            <CopyRight />
        </div>
    );
};

export default SubscriptionSelection; 